<script lang="ts" setup>
import type { ContactUsConfig } from '../configs/types'
import { useGotoBack } from '@/composables'

const props = defineProps<{
  config: ContactUsConfig
  des: string
  email: string
  loading: boolean
  handleConfirm: () => void
}>()

const emit = defineEmits<{
  (event: 'update:des', value: string): void
  (event: 'update:email', value: string): void
}>()

// 与 BasicNavbar 内部同一个返回逻辑（历史栈回退，兜底回首页）
const gotoBack = useGotoBack()

function updateField(field: 'des' | 'email', event: Event) {
  emit(`update:${field}`, (event.target as HTMLInputElement | HTMLTextAreaElement).value)
}

const { colors } = props.config
const bgPrimary = colors.bgPrimary
const textPrimary = colors.textPrimary
const textTertiary = colors.textTertiary
const textPlaceholder = colors.textPlaceholder
const accentRed = colors.accentRed
const whiteColor = colors.whiteColor
// 可选字段缺省时回退到通用字段，保证与老配置行为一致
const cardBg = colors.cardBg || colors.bgTertiary
const borderColor = colors.borderColor || 'transparent'
const submitGradient = colors.submitGradient || colors.primaryColor
const submitShadow = colors.submitShadow || 'none'
</script>

<template>
  <div class="nm-contact-page">
    <!-- 顶栏：左侧返回 + 居中标题（不改公共 BasicNavbar，返回逻辑复用 useGotoBack） -->
    <header class="nm-navbar">
      <button class="nm-back" type="button" aria-label="Back" @click="gotoBack()">
        <i-lucide-chevron-left class="nm-back-icon" />
      </button>
      <h1 class="nm-navbar-title">
        {{ config.texts.pageTitle || '' }}
      </h1>
    </header>

    <div class="nm-form">
      <!-- 反馈内容 -->
      <div class="nm-field">
        <div class="nm-label">
          <span class="nm-required">*</span>{{ config.texts.feedbackTitle }}
        </div>
        <div class="nm-textarea-wrap">
          <textarea
            :value="des"
            class="nm-textarea"
            :placeholder="config.texts.feedbackPlaceholder"
            maxlength="100"
            @input="updateField('des', $event)"
          />
          <span class="nm-count">{{ des.length }}/100</span>
        </div>
      </div>

      <!-- 邮箱 -->
      <div class="nm-field">
        <div class="nm-label">
          <span class="nm-required">*</span>{{ config.texts.emailTitle }}
        </div>
        <input
          :value="email"
          type="text"
          class="nm-input"
          :placeholder="config.texts.emailPlaceholder"
          @input="updateField('email', $event)"
        >
      </div>

      <!-- 提交 -->
      <button
        :disabled="des === '' || email === '' || loading"
        class="nm-submit"
        :class="{ 'is-loading': loading }"
        @click="handleConfirm"
      >
        {{ loading ? config.texts.submittingButton : config.texts.submitButton }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.nm-contact-page {
  min-height: 100vh;
  background: v-bind(bgPrimary);
  font-family: var(--nm-font-sans, 'Nunito Sans', system-ui, -apple-system, 'Helvetica Neue', sans-serif);
  -webkit-tap-highlight-color: transparent;
}

/* ---------- 顶栏 ---------- */
.nm-navbar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 44px;
  box-sizing: border-box;
}

.nm-back {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  background: transparent;
  color: v-bind(textPrimary);
  cursor: pointer;
}

.nm-back-icon {
  width: 24px;
  height: 24px;
}

.nm-navbar-title {
  margin: 0;
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', system-ui, sans-serif);
  font-size: 19px;
  font-weight: 700;
  line-height: 1.2;
  color: v-bind(textPrimary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---------- 表单 ---------- */
.nm-form {
  padding: 10px 16px 40px;
}

/* 设计规格：字段间距 10 */
.nm-field {
  margin-bottom: 10px;
}

.nm-label {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: v-bind(textPrimary);
}

.nm-required {
  margin-right: 4px;
  font-size: 14px;
  font-weight: 800;
  line-height: 20px;
  color: v-bind(accentRed);
}

.nm-textarea-wrap {
  position: relative;
}

.nm-textarea {
  display: block;
  width: 100%;
  height: 150px;
  box-sizing: border-box;
  /* 底部留出字数统计位置 */
  padding: 14px 14px 28px;
  border: 1px solid v-bind(borderColor);
  border-radius: 14px;
  background: v-bind(cardBg);
  color: v-bind(textPrimary);
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  -webkit-appearance: none;
  appearance: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.nm-count {
  position: absolute;
  right: 14px;
  bottom: 10px;
  font-size: 12px;
  line-height: 16px;
  color: v-bind(textTertiary);
  pointer-events: none;
}

.nm-input {
  display: block;
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  padding: 0 14px;
  border: 1px solid v-bind(borderColor);
  border-radius: 14px;
  background: v-bind(cardBg);
  color: v-bind(textPrimary);
  font-family: inherit;
  font-size: 14px;
  line-height: 46px;
  -webkit-appearance: none;
  appearance: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.nm-textarea::placeholder,
.nm-input::placeholder {
  font-size: 13px;
  line-height: 1.5;
  color: v-bind(textPlaceholder);
}

.nm-textarea:focus,
.nm-input:focus {
  outline: none;
  border-color: v-bind(accentRed);
  box-shadow: 0 0 0 3px rgba(240, 80, 155, 0.12);
}

/* ---------- 提交按钮 ---------- */
.nm-submit {
  display: block;
  width: 100%;
  height: 50px;
  border: none;
  border-radius: 25px;
  background: v-bind(submitGradient);
  box-shadow: v-bind(submitShadow);
  color: v-bind(whiteColor);
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  line-height: 50px;
  cursor: pointer;
  transition: opacity 0.2s, box-shadow 0.2s;
}

.nm-submit:disabled {
  opacity: 0.45;
  box-shadow: none;
  cursor: not-allowed;
}

.nm-submit:not(:disabled):active {
  opacity: 0.85;
}

.nm-submit.is-loading {
  opacity: 0.75;
}
</style>
